import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Printer} from "../_models/Printer";

@Injectable({ providedIn: 'root' })
export class NetworkPrinterService {

  constructor(private http: HttpClient) {
  }

  getAll() {
    return this.http.get<Printer[]>(`${environment.apiUrl}/printerService/printers`);
  }

  /**
   * Print a single item label over IP. Backwards compatible — callers
   * that don't supply `options` get the same wire payload as before so
   * the backend's auto-detect path (resolving printerProfile from the
   * saved Printer document by IP) still runs untouched.
   *
   * `options.printerProfile` lets the KDS select the ESC/POS dialect at
   * print time without depending on the saved Printer.printerProfile —
   * fixes the Epson TM-L90 + XK200L "no cut between items" bug where
   * Star cut commands silently no-op on Epson firmware.
   */
  printLabel(
    deviceIP, port, orderTitle, lineItem, businessName, orderType,
    labelVersion: string = 'v3', paperSize: string = '80mm',
    itemIndex: number = 1, itemTotal: number = 1,
    printTime?: string, printDate?: string,
    options?: { printerProfile?: 'star' | 'epson'; printerModel?: string }
  ) {
    return this.http.post(`${environment.apiUrl}/printerService/printLabel`, {
      deviceIP, port, orderTitle, item: lineItem, businessName, orderType,
      labelVersion, paperSize, itemIndex, itemTotal, printTime, printDate,
      ...(options?.printerProfile ? { printerProfile: options.printerProfile } : {}),
      ...(options?.printerModel ? { printerModel: options.printerModel } : {}),
    });
  }

  printLabelDineNGoIp(deviceIP, port, orderTitle, lineItem, businessName) {
    return this.http.post(`${environment.apiUrl}/printerService/printStarLabel`, {deviceIP: deviceIP, port: port, orderTitle:orderTitle, item:lineItem, businessName:businessName});
  }

  /**
   * Issue #1: print a customer receipt over an IP receipt printer.
   * Reuses the same transport chain as printLabel (Clover-socket → Pi
   * → notification fallback) but renders a Clover-style receipt body
   * server-side. Used as the default reprint target when no Clover
   * device is currently connected to the register.
   */
  printCustomerReceipt(payload: { deviceIP: string; port: number; order: any; businessName?: string; paperSize?: '80mm' | '58mm'; totals?: { subtotal: number; tax: number; tip: number; total: number }; printIntentId?: string; }) {
    return this.http.post<{success: boolean; status?: 'received' | 'accepted_unconfirmed' | 'blocked_paper' | 'printing' | 'printed' | 'failed'; jobId?: string; deliveredVia?: string; reason?: string; error?: string; statusCapable?: boolean}>(
      `${environment.apiUrl}/printerService/printCustomerReceipt`, payload,
    );
  }

  /**
   * Phase B — poll the true physical status of a receipt job. Only meaningful
   * when the printing relay is status-capable (advertised the `receipt_status`
   * capability); for the current fleet the backend always returns
   * `status: null` and the cashier keeps trusting the transport ACK it already
   * had. This is a read-only query: it never re-sends the job, so polling can
   * never cause a duplicate print.
   */
  getReceiptJobStatus(jobId: string) {
    return this.http.get<{ jobId: string; status: 'blocked_paper' | 'printing' | 'printed' | 'failed' | null; updatedAt: number | null }>(
      `${environment.apiUrl}/printerService/receiptJobStatus/${encodeURIComponent(jobId)}`,
    );
  }

  /**
   * Issue #2: print an order ticket over an IP printer (kitchen / order
   * printer). Same transport as the receipt template but a non-totals
   * layout suitable for the back-of-house — qty, item name in bold,
   * indented modifiers, and any line note.
   */
  printOrderTicket(payload: { deviceIP: string; port: number; order: any; paperSize?: '80mm' | '58mm'; }) {
    return this.http.post<{success: boolean; deliveredVia?: string; reason?: string; error?: string}>(
      `${environment.apiUrl}/printerService/printOrderTicket`, payload,
    );
  }

  /**
   * Issue #7: send a cash-drawer kick command to an IP receipt printer
   * (ESC p). Used at payment completion when the drawer is wired through
   * a thermal printer rather than a Clover device. Backend openCashDrawer
   * already invokes this internally as a final fallback strategy, so most
   * callers should keep using openCashDrawer; this is exposed for direct
   * testing / configuration screens.
   */
  cashDrawerKickIp(payload: { deviceIP: string; port: number; }) {
    return this.http.post<{success: boolean; deliveredVia?: string; reason?: string; error?: string}>(
      `${environment.apiUrl}/printerService/cashDrawerKickIp`, payload,
    );
  }

  /**
   * Print a timesheet receipt when an employee clocks out. Shows date, clock
   * events, total worked time, and estimated pay when a wage rate is available.
   */
  printTimesheetReceipt(payload: {
    deviceIP: string;
    port: number;
    employeeName: string;
    date: string;
    events: Array<{type: string; time: string}>;
    workedSeconds: number;
    breakSeconds?: number;
    wageCentsPerHour?: number;
    payType?: string;
    businessName?: string;
    paperSize?: '80mm' | '58mm';
  }) {
    return this.http.post<{success: boolean; deliveredVia?: string; reason?: string; error?: string}>(
      `${environment.apiUrl}/printerService/printTimesheetReceipt`, payload,
    );
  }

  openCashDrawer(deviceModel?: string, deviceSerial?: string, deviceId?: string) {
    return this.http.post(`${environment.apiUrl}/printerService/openCashDrawer`, { deviceModel, deviceSerial, deviceId });
  }

  updatePrinter(id: string, data: Partial<Printer>) {
    return this.http.put<Printer>(`${environment.apiUrl}/printerService/updatePrinter/${id}`, data);
  }

  printLabelRouted(orderPayload: any, trigger?: 'reprint' | 'on_save' | 'on_pay' | 'after_payment') {
    // Pass trigger='reprint' from manual Fire-Order buttons to bypass the
    // backend labelPrintDedupCache (24h TTL). Auto-fire callers — KDS
    // socket-driven fan-out, webhook reactions — must leave trigger unset
    // so the dedup applies; otherwise every webhook event re-prints the
    // same kitchen ticket. Regression introduced by commit 8301b3c4 when
    // the trigger was hard-coded on this helper instead of routed through
    // the caller; this is the call site that ticket #1612 trips at.
    const body = trigger ? { ...orderPayload, trigger } : orderPayload;
    return this.http.post(`${environment.apiUrl}/printerService/printLabelRouted`, body);
  }

  scanNetwork() {
    return this.http.post<any[]>(`${environment.apiUrl}/printerService/scanNetwork`, {});
  }

  savePrinter(printer: Partial<Printer>) {
    return this.http.post(`${environment.apiUrl}/printerService/savePrinter`, printer);
  }

  deletePrinter(id: string) {
    return this.http.delete(`${environment.apiUrl}/printerService/deletePrinter/${id}`);
  }

  getConnectedDevices() {
    return this.http.get<{pi: boolean, clover: boolean, piInventory?: boolean}>(`${environment.apiUrl}/printerService/connectedDevices`);
  }

  testPrint(printer: Printer) {
    // Saved-printer test. Sends the type/profile/paperSize the row was
    // saved with so the backend renders the actual template for that
    // role (receipt vs order ticket vs sticky label) — pre-fix this
    // always emitted a sticky-label test regardless of the saved type,
    // which made the "Test" button useless on receipt printers.
    const ct = (printer as any).connectionType;
    const wired = ct === 'usb' || ct === 'serial';
    return this.http.post(`${environment.apiUrl}/printerService/printTestPage`, {
      deviceIP: printer.ip,
      port: wired ? 0 : printer.port,
      printerType: (printer as any).printerType,
      printerProfile: (printer as any).printerProfile,
      printerModel: (printer as any).model,
      paperSize: (printer as any).paperSize || '80mm',
      // Wired rows carry their transport + USB identity so the backend routes
      // the test to the owning Runner rather than TCP:9100. Networked rows omit
      // these and behave exactly as before.
      ...(ct ? { connectionType: ct } : {}),
      ...((printer as any).usbVendorId != null ? { usbVendorId: (printer as any).usbVendorId } : {}),
      ...((printer as any).usbProductId != null ? { usbProductId: (printer as any).usbProductId } : {}),
      ...((printer as any).usbSerial ? { usbSerial: (printer as any).usbSerial } : {}),
      ...((printer as any).serialBaud ? { serialBaud: (printer as any).serialBaud } : {}),
    });
  }

  /**
   * Pre-save test print for the printer-config dialog. Lets the cashier
   * verify the chosen IP + port + printer-type combination BEFORE
   * committing the row to the DB. `form` carries the in-progress edits
   * — printerType drives which template the backend renders (sticky
   * label vs receipt vs kitchen order ticket), printerProfile picks the
   * ESC/POS dialect, paperSize sets column width. Saved-printer
   * testPrint() above shares the same endpoint so the two paths can't
   * diverge over time.
   */
  testPrintInline(form: {
    ip: string; port: number;
    printerType?: string;
    printerProfile?: 'star' | 'epson';
    printerModel?: string;
    paperSize?: '80mm' | '58mm';
    // Wired (USB / USB-serial) test. When present the backend's
    // _resolvePrinterWiring picks the wired transport and broadcasts the
    // rendered bytes to the owning Android Runner instead of TCP:9100.
    connectionType?: 'ip' | 'usb' | 'serial';
    usbVendorId?: number;
    usbProductId?: number;
    usbSerial?: string;
    serialBaud?: number;
  }) {
    return this.http.post(`${environment.apiUrl}/printerService/printTestPage`, {
      deviceIP: form.ip,
      // Wired rows send port 0 (no TCP target); keep it explicit rather than
      // defaulting to 9100 so the backend never treats a wired test as networked.
      port: form.connectionType === 'usb' || form.connectionType === 'serial'
        ? (form.port || 0) : (form.port || 9100),
      printerType: form.printerType,
      printerProfile: form.printerProfile,
      printerModel: form.printerModel,
      paperSize: form.paperSize || '80mm',
      ...(form.connectionType ? { connectionType: form.connectionType } : {}),
      ...(form.usbVendorId != null ? { usbVendorId: form.usbVendorId } : {}),
      ...(form.usbProductId != null ? { usbProductId: form.usbProductId } : {}),
      ...(form.usbSerial ? { usbSerial: form.usbSerial } : {}),
      ...(form.serialBaud ? { serialBaud: form.serialBaud } : {}),
    });
  }

}
